import { Controller } from "@hotwired/stimulus"

// Stimulus controller for payment methods
export default class extends Controller {
  static targets = ["form", "paymentElement", "submitButton", "errors", "amount1", "amount2", "verifyErrors", "verifyButton", "methodRadio", "amountsSection", "descriptorSection", "descriptorCode"]
  static values = {
    key: String,
    clientSecret: String,
    userEmail: String,
    userName: String,
    paymentMethodId: String,
    setupIntentId: String
  }

  connect() {
    // Check if this is a verification form (has amount inputs) or payment form (has payment element)
    if (this.hasSetupIntentIdValue && this.hasAmount1Target) {
      // This is a verification form - initialize Stripe
      this.initializeStripeForVerification();

      // Set up verification form submission
      this.element.addEventListener('submit', async (event) => {
        event.preventDefault();
        await this.handleVerification();
      });
    } else {
      // This is a payment form - initialize Stripe Elements
      // Wait for Stripe script to load
      if (document.getElementById('stripe-js')) {
        document.getElementById('stripe-js').addEventListener('load', () => {
          this.initializeStripe()
        })
      } else {
        console.log("[YYY] Stripe script not found")
      }

      // If script already loaded, initialize immediately
      if (window.Stripe) {
        this.initializeStripe()
      }
    }
  }

  initializeStripeForVerification() {
    // Wait for Stripe script to load for verification
    if (document.getElementById('stripe-js')) {
      document.getElementById('stripe-js').addEventListener('load', () => {
        console.log('[YYY] Stripe loaded for verification');
        this.stripe = window.Stripe(this.keyValue);
      })
    }

    // If script already loaded, initialize immediately
    if (window.Stripe) {
      console.log('[YYY] Stripe already loaded for verification');
      this.stripe = window.Stripe(this.keyValue);
    }
  }

  initializeStripe() {
    console.log("[YYY] Test")
    if (!this.hasKeyValue || !this.hasClientSecretValue) {
      console.log("[YYY] Missing required values", {
        hasKey: this.hasKeyValue,
        hasSecret: this.hasClientSecretValue
      });
      return;
    }

    this.stripe = window.Stripe(this.keyValue);

    console.log('[YYY] User email:', this.userEmailValue);
    console.log('[YYY] User name:', this.userNameValue);

    // Detect dark mode
    const isDarkMode = document.documentElement.classList.contains('dark');

    this.elements = this.stripe.elements({
      clientSecret: this.clientSecretValue,
      appearance: {
        theme: isDarkMode ? 'night' : 'stripe',
        variables: {
          colorPrimary: '#3b82f6', // primary-600
          colorBackground: isDarkMode ? '#1f2937' : '#ffffff', // gray-800 : white
          colorText: isDarkMode ? '#f9fafb' : '#111827', // gray-50 : gray-900
          colorDanger: '#ef4444', // red-500
          fontFamily: 'system-ui, sans-serif',
          spacingUnit: '4px',
          borderRadius: '6px'
        }
      }
    });

    // Build payment element options with default values
    const paymentElementOptions = {
      layout: 'tabs',
      paymentMethodOrder: ['card', 'us_bank_account']
    };

    // Add default values for pre-filling
    const billingDetails = {};
    if (this.hasUserEmailValue && this.userEmailValue) {
      billingDetails.email = this.userEmailValue;
    }
    if (this.hasUserNameValue && this.userNameValue) {
      billingDetails.name = this.userNameValue;
    }

    if (Object.keys(billingDetails).length > 0) {
      paymentElementOptions.defaultValues = {
        billingDetails: billingDetails
      };
    }

    console.log('[YYY] Payment element options:', paymentElementOptions);

    // Create unified payment element with multiple payment methods
    this.paymentElement = this.elements.create('payment', paymentElementOptions);
    this.paymentElement.mount(this.paymentElementTarget);

    // Handle validation errors
    this.paymentElement.on('change', (event) => {
      console.log('[YYY] Payment element change:', event);
      if (event.error) {
        this.errorsTarget.textContent = event.error.message;
        this.submitButtonTarget.disabled = true;
      } else {
        this.errorsTarget.textContent = '';
        this.submitButtonTarget.disabled = !event.complete;
      }
    });

    // Handle form submission
    console.log('[YYY] Setting up form submit listener on:', this.formTarget);
    this.formTarget.addEventListener('submit', async (event) => {
      console.log('[YYY] Form submit event fired');
      event.preventDefault();
      await this.handleSubmit();
    });
  }

  async handleSubmit() {
    console.log("[YYY] handleSubmit")
    this.submitButtonTarget.disabled = true;
    this.errorsTarget.textContent = '';

    try {
      const { setupIntent, error } = await this.stripe.confirmSetup({
        elements: this.elements,
        confirmParams: {
          return_url: window.location.href
        },
        redirect: 'if_required'
      });

      console.log('Setup result:', { setupIntent, error });

      if (error) {
        console.error('Stripe error:', error);
        this.errorsTarget.textContent = error.message;
        this.submitButtonTarget.disabled = false;
      } else if (setupIntent) {
        console.log('SetupIntent status:', setupIntent.status);
        console.log('Payment method:', setupIntent.payment_method);

        // Handle different SetupIntent statuses
        if (setupIntent.status === 'succeeded') {
          // Card or verified bank account - ready to use
          console.log('Status succeeded, submitting payment method');
          this.submitPaymentMethod(setupIntent.payment_method, setupIntent.id);
        } else if (setupIntent.status === 'processing' || setupIntent.status === 'requires_action') {
          // Bank account pending micro-deposit verification
          // Payment method is still created, just needs verification
          console.log('Status processing/requires_action');
          if (setupIntent.payment_method) {
            console.log('Payment method exists, submitting with SetupIntent ID for verification');
            this.submitPaymentMethod(setupIntent.payment_method, setupIntent.id);
          } else {
            console.log('No payment method yet');
            this.errorsTarget.textContent = 'Bank account submitted. Please check your email for verification instructions.';
            this.submitButtonTarget.disabled = false;
          }
        } else {
          console.log('Unexpected status:', setupIntent.status);
          this.errorsTarget.textContent = `Unexpected status: ${setupIntent.status}`;
          this.submitButtonTarget.disabled = false;
        }
      } else {
        console.log('No setupIntent and no error - unexpected');
      }
    } catch (err) {
      console.error('[YYY] Exception:', err);
      this.errorsTarget.textContent = err.message || "An error occurred";
      this.submitButtonTarget.disabled = false;
    }
  }

  submitPaymentMethod(paymentMethodId, setupIntentId = null) {
    const pmInput = document.createElement('input');
    pmInput.setAttribute('type', 'hidden');
    pmInput.setAttribute('name', 'payment_method_id');
    pmInput.setAttribute('value', paymentMethodId);
    this.formTarget.appendChild(pmInput);

    // Store SetupIntent ID for bank account verification
    if (setupIntentId) {
      const siInput = document.createElement('input');
      siInput.setAttribute('type', 'hidden');
      siInput.setAttribute('name', 'setup_intent_id');
      siInput.setAttribute('value', setupIntentId);
      this.formTarget.appendChild(siInput);
    }

    this.formTarget.submit();
  }

  toggleVerificationMethod(event) {
    // Get selected verification method
    const selectedMethod = this.methodRadioTargets.find(radio => radio.checked)?.value;

    if (selectedMethod === 'amounts') {
      this.amountsSectionTarget.classList.remove('hidden');
      this.descriptorSectionTarget.classList.add('hidden');
    } else if (selectedMethod === 'descriptor') {
      this.amountsSectionTarget.classList.add('hidden');
      this.descriptorSectionTarget.classList.remove('hidden');
    }
  }

  async handleVerification() {
    console.log('[YYY] handleVerification called');

    // Determine which verification method is selected
    const selectedMethod = this.methodRadioTargets.find(radio => radio.checked)?.value;
    console.log('[YYY] Selected method:', selectedMethod);

    let verificationData;

    if (selectedMethod === 'amounts') {
      // Validate amounts
      if (!this.hasAmount1Target || !this.hasAmount2Target) {
        console.error('Amount inputs not found');
        return;
      }

      const amount1 = parseInt(this.amount1Target.value);
      const amount2 = parseInt(this.amount2Target.value);

      if (!amount1 || !amount2 || amount1 < 1 || amount1 > 99 || amount2 < 1 || amount2 > 99) {
        this.verifyErrorsTarget.textContent = 'Please enter valid amounts between 1 and 99 cents';
        return;
      }

      verificationData = { amounts: [amount1, amount2] };
    } else if (selectedMethod === 'descriptor') {
      // Validate descriptor code
      if (!this.hasDescriptorCodeTarget) {
        console.error('Descriptor code input not found');
        return;
      }

      const descriptorCode = this.descriptorCodeTarget.value.trim().toUpperCase();

      if (!descriptorCode || descriptorCode.length !== 6) {
        this.verifyErrorsTarget.textContent = 'Please enter a valid 6-character descriptor code';
        return;
      }

      verificationData = { descriptor_code: descriptorCode };
    } else {
      this.verifyErrorsTarget.textContent = 'Please select a verification method';
      return;
    }

    this.verifyButtonTarget.disabled = true;
    this.verifyErrorsTarget.textContent = '';

    try {
      // Get the SetupIntent client secret from Stripe
      const response = await fetch(`/accounts/${window.location.pathname.split('/')[2]}/settings/payment_methods/${this.paymentMethodIdValue}/verification_secret`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get verification secret');
      }

      console.log('[YYY] Got client secret, verifying with:', verificationData);

      // Verify the micro-deposits
      const { setupIntent, error } = await this.stripe.verifyMicrodepositsForSetup(
        data.client_secret,
        verificationData
      );

      if (error) {
        console.error('[YYY] Verification error:', error);
        this.verifyErrorsTarget.textContent = error.message;
        this.verifyButtonTarget.disabled = false;
      } else if (setupIntent && setupIntent.status === 'succeeded') {
        console.log('[YYY] Verification successful!');
        // Reload page to show updated status
        window.location.reload();
      } else {
        console.log('[YYY] Unexpected status:', setupIntent?.status);
        this.verifyErrorsTarget.textContent = 'Verification failed. Please try again.';
        this.verifyButtonTarget.disabled = false;
      }
    } catch (err) {
      console.error('[YYY] Verification exception:', err);
      this.verifyErrorsTarget.textContent = err.message || 'An error occurred during verification';
      this.verifyButtonTarget.disabled = false;
    }
  }

  disconnect() {
    // Clean up Stripe elements if needed
    if (this.paymentElement) {
      this.paymentElement.unmount();
    }
  }
}
